<template>
  <div
    ref="selectWrapper"
    v-click-outside:[popperPaneRef]="handleClose"
    :class="wrapperKls"
    @click.stop="toggleMenu"
  >
    <sg-tooltip
      ref="tooltipRef"
      v-model:visible="dropMenuVisible"
      :show-arrow = "false"
      :placement="(placement as any)"
      :transfer="transfer"
      :drop-class-name="['sg-select__popper']"
      :fallback-placements="['bottom-start', 'top-start', 'right', 'left']"
      :theme="toolTipTheme"
      pure
      trigger="click"
      persistent
      transition="sg-zoom-in-top"
      :stop-popper-mouse-event="false"
      :gpu-acceleration="false"
      @show="handleMenuEnter"
    >
      <template #default>
        <div class="select-trigger">
          <div
            v-if="multiple && !attrs.readonly"
            ref="tagsRef"
            class="sg-select__tags"
            :style="selectTagsStyle"
          >
            <sg-multiple-tags
              ref="multipleTagsRef"
              :list="selected"
              :collapse="collapseTags"
              :ellipsis="ellipsisTag"
              :not-only-one="notOnlyOne"
              :value-key="valueKey"
              :prefix-width="prefixWidth"
              :disabled="selectDisabled"
              :tag-size="tagSize"
              :input-width="inputWidth"
              :show-tooltip="showMultipleTip"
              :tooltip-offset="toolTipOffset"
              :tooltip-theme="toolTipTheme"
              :tooltip-transfer="multipleTipTransfer"
              @deleted="deleteTag"
              @reset="handleMultipleTagRest"
              @resize="resetInputHeight"
            />
            <input
              v-if="filterable"
              ref="inputRef"
              v-model="query"
              type="text"
              class="sg-select__input sg-filterable-input"
              :class="[selectSize ? `is-${selectSize}` : '']"
              :disabled="selectDisabled"
              :placeholder="filterable ? '' : (attrs.placeholder as string)"
              :style="{
                width: `${inputLength / (inputWidth - 42)}%`,
                maxWidth: `${inputWidth - 42}px`
              }"
              @focus="handleFocus"
              @blur="handleBlur"
              @keyup="managePlaceholder"
              @keydown="resetInputState"
              @keydown.down.prevent="navigateOptions('next')"
              @keydown.up.prevent="navigateOptions('prev')"
              @keydown.esc.stop.prevent="visible = false"
              @keydown.enter.stop.prevent="selectOption"
              @keydown.delete="deletePrevTag"
              @keydown.tab="visible = false"
              @compositionstart="handleComposition"
              @compositionupdate="handleComposition"
              @compositionend="handleComposition"
              @input="debouncedQueryChange"
            />
          </div>
          <sg-tooltip
            :drop-class-name="'show-overflow-text-popper'"
            :disabled="handelShowOverflowText"
            :fallback-placements="['bottom', 'top', 'right', 'left']"
            :theme="toolTipTheme"
            :placement="(toolTipPlacement as any)"
            :transfer="false"
            :verticalOffset="toolTipOffset"
          >
            <template #default>
              <sg-input
                :id="id"
                ref="reference"
                v-model="selectedLabel"
                type="text"
                ellipsisTag
                :placeholder="currentPlaceholder"
                :size="selectSize"
                :disabled="selectDisabled"
                :readonly="isReadonly || attrs.readonly || isShowFilterInput"
                :validate-event="false"
                :class="{ 'is-focus': visible }"
                :tabindex="multiple && filterable ? -1 : undefined"
                @on-focus="handleFocus"
                @on-blur="handleBlur"
                @on-input="debouncedOnInputChange"
                @paste="debouncedOnInputChange"
                @compositionstart="handleComposition"
                @compositionupdate="handleComposition"
                @compositionend="handleComposition"
                @keydown.down.stop.prevent="navigateOptions('next')"
                @keydown.up.stop.prevent="navigateOptions('prev')"
                @keydown.enter.stop.prevent="selectOption"
                @keydown.esc.stop.prevent="visible = false"
                @keydown.tab="visible = false"
                @mouseenter="inputHovering = true"
                @mouseleave="inputHovering = false"
              >
                <template v-if="$slots.prefix" #prefix>
                  <div
                    style="
                      height: 100%;
                      display: flex;
                      justify-content: center;
                      align-items: center;
                    "
                  >
                    <slot name="prefix"></slot>
                  </div>
                </template>
                <template #suffix>
                  <template v-if="!attrs.readonly">
                    <sg-icon
                      v-if="iconComponent"
                      v-show="!showClose"
                      :class="['sg-select__caret', 'sg-input__icon', iconReverse]"
                    >
                      <component :is="iconComponent" />
                    </sg-icon>
                    <sg-icon
                      v-if="showClose"
                      class="sg-select__caret sg-input__icon delete"
                      @click="handleClearClick"
                    >
                      <Close />
                    </sg-icon>
                  </template>
                  <template v-else>
                    <sg-icon class="sg-select__caret sg-input__icon">
                      <component :is="iconComponent" />
                    </sg-icon>
                  </template>
                </template>
              </sg-input>
            </template>
            <template #content>
              <span>{{ selectedLabel }}</span>
            </template>
          </sg-tooltip>
        </div>
      </template>
      <template #content v-if="isListShow">
        <sg-input v-if="isShowFilterInput" class="sg-filter-input" v-model="filterInputText"  @on-input="debouncedOnInputChange">
          <template #append>
            <SgIcon>
              <Search />
            </SgIcon>
          </template>
        </sg-input>
        <sg-select-menu  >
          <sg-scrollbar
            :height="isShowFilterInput ? scrollheight : scrollheight - 36 "
            v-show="options.size > 0"
            ref="scrollbar"
            tag="ul"
            wrap-class="sg-select-dropdown__wrap"
            view-class="sg-select-dropdown__list"
            :class="{
              'is-empty': !allowCreate && query && filteredOptionsCount === 0,
            }"
          >
            <sg-option v-if="showNewOption" :value="query" :is-created="true" />
            <slot></slot>
          </sg-scrollbar>
          <template
            v-if="
              emptyText &&
              (!allowCreate  || (allowCreate && options.size === 0))
            "
          >
            <slot v-if="$slots.empty" name="empty"></slot>
            <p v-else class="sg-select-dropdown__empty">
              {{ emptyText }}
            </p>
          </template>
        </sg-select-menu>
      </template>
    </sg-tooltip>
  </div>
</template>

<script lang="ts">
import {
  toRefs,
  defineComponent,
  onMounted,
  onBeforeUnmount,
  nextTick,
  reactive,
  provide,
  computed,
  unref,
} from 'vue'
import { ClickOutside } from '@sgui-plus/directives'
import { useFocus, useLocale } from '@sgui-plus/hooks'
import SgInput from '@sgui-plus/components/input'
import SgTooltip, { useTooltipContentProps } from '@sgui-plus/components/tooltip'
import SgScrollbar from '@sgui-plus/components/scrollbar'
import SgTag from '@sgui-plus/components/tag'
import SgIcon from '@sgui-plus/components/icon'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import {
  addResizeListener,
  removeResizeListener,
} from '@sgui-plus/utils/resize-event'
import { isValidComponentSize } from '@sgui-plus/utils/validators'
import { Close, ArrowUp, Search } from '@sgui-plus/icons-vue'
import SgOption from './option.vue'
import SgSelectMenu from './select-dropdown.vue'
import SgMultipleTags from './multiple-tags.vue'
import { useSelect, useSelectStates } from './useSelect'
import { SelectContextKey } from './token'

import type { PropType, Component } from 'vue'
import type { ComponentSize } from '@sgui-plus/utils/size'
import type { SelectContext } from './token'

export default defineComponent({
  name: 'SgSelect',
  componentName: 'SgSelect',
  components: {
    Close,
    SgInput,
    SgSelectMenu,
    SgOption,
    SgTag,
    SgMultipleTags,
    SgScrollbar,
    SgTooltip,
    SgIcon,
    Search
  },
  directives: { ClickOutside },
  props: {
    name: String,
    id: String,
    modelValue: {
      type: [Array, String, Number, Boolean, Object],
      default: undefined,
    },
    size: {
      type: String as PropType<ComponentSize>,
      validator: isValidComponentSize,
    },
    disabled: Boolean,
    clearable: Boolean,
    filterable: Boolean,
    filterHighlight: Boolean,
    allowCreate: Boolean,
    placement: {
      type: String,
      default: 'bottom',
    },
    noMatchText: String,
    noDataText: String,
    filterMethod: Function,
    multiple: Boolean,
    multipleLimit: {
      type: Number,
      default: 0,
    },
    defaultFirstOption: Boolean,
    valueKey: {
      type: String,
      default: 'value',
    },
    labelInValue: {
      type: Boolean,
      default: false,
    },
    listInitHidden: {
      type: Boolean,
      default: false,
    },
    showSingleFilterInput: {
      type: Boolean,
      default: false,
    },
    // 初始化时显示默认选中的值
    defaultLabel: {
      type: [String, Number, Array]
    },
    transfer: useTooltipContentProps.transfer,
    collapseTags: Boolean,
    showMultipleTip: {
      type: Boolean,
      default: false,
    },
    ellipsisTag: {
      type: Boolean,
      default: false,
    },
    notOnlyOne: {
      type: Boolean,
      default: false,
    },
    dropWidthLinked: {
      type: Boolean,
      default: false,
    },
    scrollwidth: {
      type: Number,
      default: 0
    },
    scrollheight: {
      type: Number,
      default: 150
    },
    suffixIcon: {
      type: [String, Object] as PropType<string | Component>,
      default: ArrowUp,
    },
    toolTipTheme: {
      type: String as PropType<'light' | 'dark' | string>,
      default: 'light',
    },
    fixedHeight: Boolean,
    showOverflowText: {
      type: Boolean,
      default: false,
    },
    toolTipPlacement: {
      type: String,
      default: 'top',
    },
    toolTipOffset: {
      type: Number,
      default: 0
    },
    multipleTipTransfer: {
      type: Boolean,
      default: false
    },
    contentSeparator: {
      type: String,
      default: ','
    },
    validateEvent: {
      type: Boolean,
      default: true,
    }
  },
  emits: [
    UPDATE_MODEL_EVENT,
    'on-change',
    'on-remove-tag',
    'on-clear',
    'on-open-change',
    'on-focus',
    'on-blur',
    'on-tag-rest'
  ],

  setup(props, ctx) {
    const { t } = useLocale()
    const states = useSelectStates(props)
    const {
      isListShow,
      optionsArray,
      selectSize,
      isReadonly,
      handleResize,
      tagSize,
      debouncedOnInputChange,
      debouncedQueryChange,
      deletePrevTag,
      deleteTag,
      deleteSelected,
      handleOptionSelect,
      handleMultipleTagRest,
      scrollToOption,
      setSelected,
      resetInputHeight,
      managePlaceholder,
      showClose,
      selectDisabled,
      iconComponent,
      iconReverse,
      showNewOption,
      emptyText,
      toggleLastOptionHitState,
      resetInputState,
      handleComposition,
      onOptionCreate,
      onOptionDestroy,
      handleMenuEnter,
      handleFocus,
      blur,
      handleBlur,
      handleClearClick,
      handleClose,
      toggleMenu,
      selectOption,
      navigateOptions,
      dropMenuVisible,
      handelShowOverflowText,
      updatePopper,

      reference,
      inputRef,
      tooltipRef,
      tagsRef,
      multipleTagsRef,
      selectWrapper,
      scrollbar,
      queryChange,
      groupQueryChange
    } = useSelect(props, states, ctx)

    const { focus } = useFocus(reference)

    const {
      filterInputText,
      inputWidth,
      selected,
      inputLength,
      filteredOptionsCount,
      visible,
      softFocus,
      selectedLabel,
      hoverIndex,
      query,
      inputHovering,
      currentPlaceholder,
      menuVisibleOnFocus,
      isOnComposition,
      isSilentBlur,
      options,
      cachedOptions,
      optionsCount,
      prefixWidth,
      tagInMultiLine
    } = toRefs(states)
   // 是否显示搜索框（单选 &多选）
   const isShowFilterInput = computed(() => {
    return props.filterable && props.showSingleFilterInput
   })
    const wrapperKls = computed(() => {
      const classList = ['sg-select']
      const _selectSize = unref(selectSize)
      if (_selectSize) {
        classList.push(`sg-select--${_selectSize}`)
      }
      if (props.disabled) {
        classList.push(`sg-select--disabled`)
      }
      return classList
    })

    const selectTagsStyle = computed(() => ({
      maxWidth: `${unref(inputWidth) - 32}px`,
      width: '100%',
      height: props.fixedHeight ? '28px' : '',
      overflow: props.fixedHeight ? 'auto' : ''
    }))

    provide(
      SelectContextKey,
      reactive({
        props,
        options,
        optionsArray,
        cachedOptions,
        optionsCount,
        filteredOptionsCount,
        hoverIndex,
        handleOptionSelect,
        onOptionCreate,
        onOptionDestroy,
        selectWrapper,
        selected,
        setSelected,
        queryChange,
        groupQueryChange,
      }) as unknown as SelectContext
    )
    onMounted(() => {
      states.cachedPlaceHolder = currentPlaceholder.value =
        ctx.attrs.placeholder as string || t('sg.select.placeholder')
      if (
        props.multiple &&
        Array.isArray(props.modelValue) &&
        props.modelValue.length > 0
      ) {
        currentPlaceholder.value = ''
      }
      addResizeListener(selectWrapper.value as any, handleResize)
      if (reference.value && reference.value.$el) {
        const sizeMap = {
          large: 36,
          default: 32,
          small: 28,
        }
        const input = reference.value.input as HTMLInputElement
        states.initialInputHeight =
          input.getBoundingClientRect().height || sizeMap[selectSize.value]
      }
      nextTick(() => {
        if (!reference.value) return
        if (reference.value.$el) {
          inputWidth.value = reference.value.$el.getBoundingClientRect().width
        }
        if (ctx.slots.prefix) {
          const inputChildNodes = reference.value.$el.childNodes
          const input = (Array.from(inputChildNodes) as HTMLElement[]).filter(
            (item) => item.tagName === 'INPUT'
          )[0]
          const prefix = reference.value.$el.querySelector('.sg-input__prefix')
          prefixWidth.value = Math.max(
            prefix.getBoundingClientRect().width + 5,
            30
          )
          if (states.prefixWidth) {
            input.style.paddingLeft = `${Math.max(states.prefixWidth, 30)}px`
          }
        }
      })
      if(isListShow.value) {
        setSelected()
      }
    })

    onBeforeUnmount(() => {
      removeResizeListener(selectWrapper.value as any, handleResize)
    })

    if (props.multiple && !Array.isArray(props.modelValue)) {
      ctx.emit(UPDATE_MODEL_EVENT, [])
    }
    if (!props.multiple && Array.isArray(props.modelValue)) {
      ctx.emit(UPDATE_MODEL_EVENT, '')
    }

    const popperPaneRef = computed(() => {
      return tooltipRef.value?.popperRef?.contentRef
    })

    return {
      isListShow,
      filterInputText,
      attrs: ctx.attrs,
      tagInMultiLine,
      prefixWidth,
      selectSize,
      isReadonly,
      handleResize,
      tagSize,
      debouncedOnInputChange,
      debouncedQueryChange,
      deletePrevTag,
      deleteTag,
      deleteSelected,
      handleOptionSelect,
      handleMultipleTagRest,
      scrollToOption,
      inputWidth,
      selected,
      inputLength,
      filteredOptionsCount,
      visible,
      softFocus,
      selectedLabel,
      hoverIndex,
      query,
      inputHovering,
      currentPlaceholder,
      menuVisibleOnFocus,
      isOnComposition,
      isSilentBlur,
      options,
      resetInputHeight,
      managePlaceholder,
      showClose,
      selectDisabled,
      iconComponent,
      iconReverse,
      showNewOption,
      emptyText,
      toggleLastOptionHitState,
      resetInputState,
      handleComposition,
      handleMenuEnter,
      handleFocus,
      blur,
      handleBlur,
      handleClearClick,
      handleClose,
      toggleMenu,
      selectOption,
      navigateOptions,
      dropMenuVisible,
      focus,
      updatePopper,

      reference,
      inputRef,
      tooltipRef,
      tagsRef,
      multipleTagsRef,
      popperPaneRef,
      selectWrapper,
      scrollbar,

      wrapperKls,
      isShowFilterInput,
      selectTagsStyle,
      handelShowOverflowText,
    }
  },
})
</script>
